import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import LoginPage from "../features/auth/pages/login";
import RegisterPage from "../features/auth/pages/register";
import SetPasswordPage from "../features/auth/pages/set-password";
import { ProtectedRoute } from "@/router/ProtectedRoute";
import { PermissionProtectedRoute } from "@/router/PermissionProtectedRoute";
import { PublicRoute } from "@/router/PublicRoute";
import { useIsLoading, useIsFullyAuthenticated } from "@/store";

const CurrentBudgetsPage = lazy(
  () => import("../features/budgets/pages/CurrentBudgetsPage"),
);
const BudgetHistoryPage = lazy(
  () => import("../features/budgets/pages/BudgetHistoryPage"),
);
const BudgetCreationPage = lazy(
  () => import("../features/budgets/pages/BudgetCreationPage"),
);
const BudgetEditPage = lazy(
  () => import("../features/budgets/pages/BudgetEditPage"),
);
const BudgetEditingPage = lazy(
  () => import("../features/budgets/pages/BudgetEditingPage"),
);
const UsersPage = lazy(() =>
  import("../features/users/pages/UsersPage").then((m) => ({
    default: m.UsersPage,
  })),
);
const RolesPage = lazy(() =>
  import("../features/roles/pages/RolesPage").then((m) => ({
    default: m.RolesPage,
  })),
);
const DimensionsPage = lazy(
  () => import("../features/dimensions/pages/DimensionsPage"),
);
const ModelsPage = lazy(() => import("../features/models/pages/ModelsPage"));
const ModelWorkspaceLayout = lazy(() =>
  import("../features/models/pages/ModelWorkspaceLayout").then((m) => ({
    default: m.ModelWorkspaceLayout,
  })),
);
const ModelScenariosPage = lazy(
  () => import("../features/models/pages/ModelScenariosPage"),
);
const MetricDetailPage = lazy(
  () => import("../features/models/pages/MetricDetailPage"),
);
const ScenarioBuilderPage = lazy(
  () => import("../features/scenarios/pages/ScenarioBuilderPage"),
);
const ReportsPage = lazy(
  () => import("../features/reports/pages/ReportsPage"),
);
const OverviewPage = lazy(
  () => import("../features/overview/pages/OverviewPage"),
);
const ModuleNotSubscribedPage = lazy(
  () => import("../features/billing/pages/ModuleNotSubscribedPage"),
);
const IntegrationsPage = lazy(
  () => import("../features/settings/pages/IntegrationsPage"),
);
const QuickBooksIntegrationPage = lazy(
  () => import("../features/settings/pages/QuickBooksIntegrationPage"),
);

const MODULE_LABELS: Record<string, string> = {
  budgets: "Budgets",
  financial_modeling: "Financial Modeling",
};

const routeFallback = (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
  </div>
);

export const AppRouter = () => {
  const isLoading = useIsLoading();
  const isAuthenticated = useIsFullyAuthenticated();

  if (isLoading) {
    return routeFallback;
  }

  return (
    <Suspense fallback={routeFallback}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route path="/reset-password" element={<SetPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />

        <Route
          path="/subscribe/:moduleKey"
          element={
            <ProtectedRoute>
              <SubscribePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/budget-data"
          element={
            <PermissionProtectedRoute
              module="budgets"
              permissions={["can_view_budgets", "can_manage_budgets"]}
              operator="OR"
            >
              <CurrentBudgetsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/budget-creation"
          element={
            <PermissionProtectedRoute
              module="budgets"
              permission="can_manage_budgets"
            >
              <BudgetCreationPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/budget-edit"
          element={
            <PermissionProtectedRoute
              module="budgets"
              permission="can_manage_budgets"
            >
              <BudgetEditPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <PermissionProtectedRoute
              permissions={["can_view_users", "can_manage_users"]}
              operator="OR"
            >
              <UsersPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/security"
          element={
            <PermissionProtectedRoute
              permissions={[
                "can_view_access_scopes",
                "can_manage_access_scopes",
                "can_view_roles",
                "can_manage_roles",
              ]}
              operator="OR"
            >
              <RolesPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/historical-data"
          element={
            <PermissionProtectedRoute
              module="budgets"
              permissions={["can_view_budgets", "can_manage_budgets"]}
              operator="OR"
            >
              <BudgetHistoryPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/dimensions"
          element={
            <PermissionProtectedRoute
              permissions={["can_view_dimensions", "can_manage_dimensions"]}
              operator="OR"
            >
              <DimensionsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations"
          element={
            <PermissionProtectedRoute
              permissions={[
                "can_view_company_settings",
                "can_manage_company_settings",
              ]}
              operator="OR"
            >
              <IntegrationsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/quickbooks"
          element={
            <PermissionProtectedRoute
              permissions={[
                "can_view_company_settings",
                "can_manage_company_settings",
              ]}
              operator="OR"
            >
              <QuickBooksIntegrationPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/budget-editing"
          element={
            <PermissionProtectedRoute
              module="budgets"
              permissions={["can_manage_budgets", "can_view_budgets"]}
              operator="OR"
            >
              <BudgetEditingPage />
            </PermissionProtectedRoute>
          }
        />

        <Route
          path="/models"
          element={
            <PermissionProtectedRoute
              module="financial_modeling"
              permissions={["can_view_models", "can_manage_models"]}
              operator="OR"
            >
              <ModelsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/models/:modelId"
          element={
            <PermissionProtectedRoute
              module="financial_modeling"
              permissions={["can_view_models", "can_manage_models"]}
              operator="OR"
            >
              <ModelWorkspaceLayout />
            </PermissionProtectedRoute>
          }
        >
          <Route index element={<Navigate to="metrics" replace />} />
          <Route path="metrics" element={<MetricDetailPage />} />
          <Route path="scenarios" element={<ModelScenariosPage />} />
          <Route
            path="scenarios/:scenarioId"
            element={<ScenarioBuilderPage />}
          />
        </Route>
        <Route path="/clients" element={<Navigate to="/models" replace />} />
        <Route path="/clients/*" element={<Navigate to="/models" replace />} />
        <Route
          path="/reporting"
          element={
            <PermissionProtectedRoute
              module="financial_modeling"
              permissions={["can_view_models", "can_manage_models"]}
              operator="OR"
            >
              <ReportsPage />
            </PermissionProtectedRoute>
          }
        />

        <Route
          path="*"
          element={<SmartRedirect isAuthenticated={isAuthenticated} />}
        />
      </Routes>
    </Suspense>
  );
};

function SubscribePage() {
  const { moduleKey } = useParams<{ moduleKey: string }>();
  const label = MODULE_LABELS[moduleKey ?? ""] ?? "this module";
  return <ModuleNotSubscribedPage moduleName={label} />;
}

interface SmartRedirectProps {
  isAuthenticated: boolean;
}

function SmartRedirect({ isAuthenticated }: SmartRedirectProps) {
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to="/login" replace />;
}
